// Public self-registration page — kullanıcılar buradan kayıt olur,
// ancak hesap admin onayı bekleyene kadar disabled kalır.
import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { UserPlus, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

export default function RegisterPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.email || !f.password) { setError("E-posta ve şifre zorunlu"); return; }
    if (f.password.length < 8) { setError("Şifre en az 8 karakter olmalı"); return; }
    if (f.password !== f.confirm) { setError("Şifreler eşleşmiyor"); return; }
    setBusy(true);
    try {
      await api.post("/auth/register", {
        email: f.email, password: f.password, name: f.name || undefined, role: "readonly",
      });
      setDone(true);
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm text-center space-y-4" data-testid="register-success">
          <CheckCircle2 className="h-16 w-16 mx-auto text-emerald-500"/>
          <h1 className="font-heading text-xl font-semibold">Kayıt Alındı</h1>
          <p className="text-sm text-muted-foreground">
            Hesabınız <b>admin onayı</b> bekliyor. Onaylandıktan sonra giriş yapabilirsiniz.
          </p>
          <Button onClick={() => nav("/login")} className="w-full" data-testid="register-goto-login">
            Giriş Sayfasına Dön
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="register-form">
        <div className="flex flex-col items-center text-center">
          <img src="/logo.jpg" alt="Has Yapı Orman Ürünleri"
               className="h-24 w-auto object-contain mb-6" data-testid="register-brand-logo"/>
          <h1 className="font-heading text-xl font-semibold tracking-tight">Yeni Hesap Oluştur</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Kayıt sonrası admin onayı gerekir
          </p>
        </div>

        {error && (
          <Alert variant="destructive" data-testid="register-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="reg-name">Ad Soyad</Label>
          <Input id="reg-name" value={f.name}
                 onChange={(e) => setF(x => ({...x, name: e.target.value}))}
                 data-testid="register-name-input"
                 placeholder="Adınız Soyadınız" autoFocus/>
        </div>

        <div className="space-y-2">
          <Label htmlFor="reg-email">E-posta *</Label>
          <Input id="reg-email" type="email" required value={f.email}
                 onChange={(e) => setF(x => ({...x, email: e.target.value}))}
                 data-testid="register-email-input"
                 autoComplete="username"
                 placeholder="ornek@hasyapimarket.com"/>
        </div>

        <div className="space-y-2">
          <Label htmlFor="reg-pw">Şifre * (min 8 karakter)</Label>
          <Input id="reg-pw" type="password" required value={f.password}
                 onChange={(e) => setF(x => ({...x, password: e.target.value}))}
                 data-testid="register-password-input"
                 autoComplete="new-password" placeholder="••••••••"/>
        </div>

        <div className="space-y-2">
          <Label htmlFor="reg-pw2">Şifre (Tekrar) *</Label>
          <Input id="reg-pw2" type="password" required value={f.confirm}
                 onChange={(e) => setF(x => ({...x, confirm: e.target.value}))}
                 data-testid="register-confirm-input"
                 autoComplete="new-password" placeholder="••••••••"/>
        </div>

        <Button type="submit" className="w-full h-11" disabled={busy}
                data-testid="register-submit-button">
          {busy ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin"/> Kayıt yapılıyor...</>
          ) : (
            <><UserPlus className="h-4 w-4 mr-2"/> Kayıt Ol</>
          )}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          Zaten hesabınız var mı?{" "}
          <Link to="/login" className="text-primary hover:underline"
                data-testid="register-goto-login-link">Giriş yapın</Link>
        </p>
      </form>
    </div>
  );
}
